import { Box, Grid } from '@mui/material';
import { useFormikContext } from 'formik';
import ModalN010 from 'practice/components/shared/n010-modal';
import { ID_STATES_PRACTICE } from 'practice/constants/id-states-practice';
import { PracticeManagementStep } from 'practice/constants/practice-management-step';
import { TypePractice } from 'practice/constants/type-practice';
import { TypeRegister } from 'practice/constants/type-register';
import { PracticeDetailVM } from 'practice/view-models/practice-detail';
import { memo, useMemo, useState } from 'react';
import { PmiButton } from 'shell/components/shared/button';
import { StepButton, StepTypography } from 'shell/components/shared/step-button';
import { StickyDiv } from 'shell/components/shared/sticky-div';
import { noop } from 'shell/utils/noop';

interface ButtonsProps {
  handleExit: () => void;
  disabled?: boolean;
  handleStep?: (id: number) => void;
  activeStep?: number;
  isDetail?: boolean;
  redirectUriHistory?: boolean;
  onPrintFolder: () => void;
  onPrintReceipt: () => void;
  toggleChiusuraPratica?: () => void;
  onPrintLetter: () => void;
  viewMessi?: string;
  tipoIniziativa?: string;
  typeRegister?: string;
  hasAttentionNote?: boolean;
  typePractice?: string;
}

const ButtonsComponent = ({
  handleExit,
  handleStep = noop,
  activeStep,
  isDetail = false,
  redirectUriHistory,
  onPrintFolder,
  onPrintReceipt,
  onPrintLetter,
  viewMessi,
  // tipoIniziativa,
  typeRegister,
  hasAttentionNote,
  typePractice,
}: ButtonsProps): React.ReactElement => {
  const formContext = useFormikContext<PracticeDetailVM>();

  const isCloseNegativePositive = useMemo(() => {
    return (
      formContext.values.practice?.codiceStatoPratica === ID_STATES_PRACTICE.POSITIVO ||
      formContext.values.practice?.codiceStatoPratica === ID_STATES_PRACTICE.NEGATIVO
    );
  }, [formContext.values.practice?.codiceStatoPratica]);

  const disabledUlterioriAccertamenti = useMemo(() => {
    if (
      isDetail ||
      hasAttentionNote ||
      isCloseNegativePositive ||
      redirectUriHistory ||
      typeRegister === TypeRegister.RegisterOfficeTemporary
    ) {
      return true;
    }

    return viewMessi !== '3';
  }, [isDetail, isCloseNegativePositive, formContext.values.practice, hasAttentionNote]);

  const disabledSospensionePratica = useMemo(() => {
    if (
      isDetail ||
      isCloseNegativePositive ||
      typeRegister === TypeRegister.RegisterOfficeTemporary ||
      typeRegister === TypeRegister.DeleteAire
    ) {
      return true;
    }

    return formContext.values.practice?.codiceStatoPratica === '5';
  }, [
    isDetail,
    isCloseNegativePositive,
    typeRegister,
    formContext.values.practice?.codiceStatoPratica,
  ]);

  const disabledStampaComunicazioniOstative = useMemo(() => {
    if (
      isDetail ||
      isCloseNegativePositive ||
      typeRegister === TypeRegister.RegisterOfficeTemporary
    ) {
      return true;
    }

    return !(
      formContext.values.practice?.tipoOperazione === '06' ||
      formContext.values.practice?.tipoOperazione === '10'
    );
  }, [
    isDetail,
    isCloseNegativePositive,
    typeRegister,
    formContext.values.practice?.tipoOperazione,
  ]);

  const steps: Array<{ label: string; disabled: boolean; paddingTop?: string }> = useMemo(() => {
    // Disabilita step registrazione ritorno accertamento messi
    let disabledRegistraRitornoAccertamentoMessi =
      isDetail || isCloseNegativePositive ? true : viewMessi !== '1';

    // disabilita step ritorno accertamento messi
    let disabledRitornoAccertamentoMessi =
      isDetail || isCloseNegativePositive ? true : viewMessi !== '2';

    // disabilita annullo accertamento
    let disabledAnnulloAccertamento =
      isDetail || isCloseNegativePositive
        ? true
        : !(formContext.values.practice?.tipoOperazione === '06');

    // disabilita ulteriore accertamento
    let disabledRichiestaUlterioreAccertamento = disabledUlterioriAccertamenti;
    // disabilita richiesta ulteriore accertamento messi
    let disabledRichiestaUlterioreAccertamentoMessi =
      isDetail || isCloseNegativePositive ? true : viewMessi !== '4';

    // disabilita registrazione sospensione pratica
    const disabledRegistraSospensionePratica = disabledSospensionePratica;
    // disabilita fine sospensione pratica
    let disabledRegistraFineSospensionePratica =
      isDetail || isCloseNegativePositive
        ? true
        : !(
            formContext.values.practice?.codiceStatoPratica === '5' &&
            formContext.values.practice?.tipoOperazione === '50'
          );

    // disabilita stampa ostativa
    let disabledStampaComunicazioneOstativa = disabledStampaComunicazioniOstative;

    // disablita ritorno comunicazione ostativa
    let disabledRitornoComunicazioneOstativa =
      isDetail || isCloseNegativePositive
        ? true
        : !(
            formContext.values.practice?.codiceStatoPratica === '5' &&
            formContext.values.practice?.tipoOperazione === '27'
          );

    if (
      formContext.values.practice?.tipoOperazione === '18' &&
      (typeRegister === TypeRegister.Residence || typeRegister === TypeRegister.Immigration)
    ) {
      disabledRegistraRitornoAccertamentoMessi = true;
      disabledRitornoAccertamentoMessi = true;
      disabledAnnulloAccertamento = true;
      disabledRichiestaUlterioreAccertamento = true;
      disabledRichiestaUlterioreAccertamentoMessi = true;
      disabledRegistraFineSospensionePratica = true;
      disabledRitornoComunicazioneOstativa = true;
      disabledStampaComunicazioneOstativa = false;
    }

    const hasOperation5059 = formContext.values.practice?.operations?.some(
      (el) => el.tipoOperazione === '50' || el.tipoOperazione === '59'
    );

    const hasOperation10 = formContext.values.practice?.operations?.find(
      (el) => el.tipoOperazione === '10'
    );

    if (
      hasOperation5059 &&
      hasOperation10 &&
      (typeRegister === TypeRegister.Residence || typeRegister === TypeRegister.Immigration)
    ) {
      disabledStampaComunicazioneOstativa = false;
    }

    return [
      {
        label: 'Registra ritorno accertamento messi',
        disabled: disabledRegistraRitornoAccertamentoMessi,
      },
      {
        label: 'Ritorno accertamento messi',
        disabled: disabledRitornoAccertamentoMessi,
      },
      {
        label: 'Annullo Accertamento',
        disabled:
          disabledAnnulloAccertamento ||
          !(typeRegister === TypeRegister.Residence || typeRegister === TypeRegister.Immigration),
      },
      {
        label: 'Richiesta ulteriori accertamenti',
        disabled: disabledRichiestaUlterioreAccertamento,
      },
      {
        label: 'Richiesta ulteriore accertamento messi',
        disabled: disabledRichiestaUlterioreAccertamentoMessi,
      },
      {
        label: 'Registra sospensione pratica',
        disabled: disabledRegistraSospensionePratica,
      },
      {
        label: 'Registra fine sospensione pratica',
        disabled: disabledRegistraFineSospensionePratica,
      },
      {
        label: 'Stampa comunicazioni ostative (art. 10 bis)',
        disabled: disabledStampaComunicazioneOstativa,
      },
      {
        label: 'Ritorno comunicazioni ostative (art. 10 bis)',
        disabled: disabledRitornoComunicazioneOstativa,
      },
      {
        label: 'Stampa comunicazione a controinteressato',
        disabled: true,
      },
      {
        label: 'Rettifica indirizzo',
        disabled: true,
        paddingTop: '1.5rem',
      },
      {
        label: 'Contatti',
        disabled: redirectUriHistory ? true : false,
      },
      {
        label: 'Allegati',
        disabled: redirectUriHistory ? true : false,
      },
      {
        label: 'Cancella persona',
        disabled: true,
      },
    ];
  }, [
    redirectUriHistory,
    disabledStampaComunicazioniOstative,
    disabledUlterioriAccertamenti,
    disabledSospensionePratica,
    formContext.values.practice,
    typeRegister,
    isCloseNegativePositive,
    isDetail,
    viewMessi,
  ]);

  const isDisabledPrintReceipt = useMemo(() => {
    if (typeRegister === TypeRegister.DeleteAire) {
      return true;
    }

    if (typeRegister === TypeRegister.SubscriptionAire) {
      return true;
    }

    return (
      !!redirectUriHistory ||
      formContext.values.practice?.codiceStatoPratica === '4' ||
      formContext.values.practice?.codiceStatoPratica === ID_STATES_PRACTICE.NEGATIVO
    );
  }, [typeRegister, redirectUriHistory]);

  const isDisabledPrintFolder = useMemo(() => {
    if (typeRegister === TypeRegister.DeleteAire) {
      return true;
    }

    if (typeRegister === TypeRegister.SubscriptionAire) {
      return true;
    }

    if (redirectUriHistory || typeRegister === TypeRegister.AddressChange) {
      return true;
    }

    return false;
  }, [typeRegister, redirectUriHistory]);

  const [openN010Modal, setOpenN010Modal] = useState(false);

  return (
    <StickyDiv>
      <Grid container spacing={1}>
        {steps.map((step, index) => (
          <Grid
            item
            xs={12}
            key={step.label?.toString()}
            paddingTop={
              step.paddingTop?.toString() ? `${step.paddingTop?.toString()} !important` : '0'
            }
          >
            <StepButton
              active={index === activeStep}
              disabled={Boolean(step.disabled)}
              variant="outlined"
              color="cdmBrandThird"
              fullWidth
              onClick={() => handleStep(index)}
            >
              <StepTypography active={index === activeStep} disabled={Boolean(step.disabled)}>
                {step.label}
              </StepTypography>
            </StepButton>
          </Grid>
        ))}
      </Grid>
      <div>
        <div className="pmi-pt-4">
          {formContext.values.practice?.codiceStatoPratica !== '0' ? (
            <div className="pmi-pb-2">
              <StepButton
                variant="outlined"
                color="cdmBrandThird"
                fullWidth
                active={PracticeManagementStep.ChiusuraPraticaErrore === activeStep}
                onClick={() => handleStep(PracticeManagementStep.ChiusuraPraticaErrore)}
                disabled={
                  isDetail ||
                  !(
                    (formContext.values.practice?.tipoOperazione === '06' ||
                      formContext.values.practice?.tipoOperazione === '10') &&
                    formContext.values.practice?.codiceStatoPratica === '1'
                  )
                }
              >
                <Box
                  sx={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Chiusura Pratica Errore
                </Box>
              </StepButton>
            </div>
          ) : undefined}
          <div className="pmi-pb-2">
            <StepButton
              variant="outlined"
              color="cdmBrandThird"
              fullWidth
              active={PracticeManagementStep.ChiusuraPositiva === activeStep}
              onClick={() => handleStep(PracticeManagementStep.ChiusuraPositiva)}
              disabled={redirectUriHistory || isDetail || isCloseNegativePositive}
            >
              <Box
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                Chiusura positiva
              </Box>
            </StepButton>
          </div>

          <div className="pmi-pb-2">
            <StepButton
              variant="outlined"
              color="cdmBrandThird"
              onClick={() => handleStep(PracticeManagementStep.ChiusuraNegativa)}
              fullWidth
              type="button"
              active={PracticeManagementStep.ChiusuraNegativa === activeStep}
              disabled={redirectUriHistory || isDetail || isCloseNegativePositive}
            >
              <Box
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                Chiusura negativa
              </Box>
            </StepButton>
          </div>
        </div>
        <div className="pmi-pb-2">
          <PmiButton
            variant="outlined"
            color="cdmBrandThird"
            onClick={() => onPrintReceipt()}
            fullWidth
            type="button"
            disabled={isDisabledPrintReceipt || typeRegister === TypeRegister.DeleteAire}
          >
            <Box
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              Stampa Ricevuta
            </Box>
          </PmiButton>
        </div>
        <div className="pmi-pb-2">
          <PmiButton
            variant="outlined"
            color="cdmBrandThird"
            disabled={isDisabledPrintFolder}
            onClick={() => onPrintFolder()}
            fullWidth
            type="button"
          >
            <Box
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              {typeRegister === TypeRegister.RegisterOfficeTemporary
                ? 'Stampa Dichiarazione'
                : 'Stampa Cartellina'}
            </Box>
          </PmiButton>
        </div>
        {typeRegister === TypeRegister.RegisterOfficeTemporary ? (
          <div className="pmi-pb-2">
            <PmiButton
              variant="outlined"
              color="cdmBrandThird"
              disabled={redirectUriHistory}
              onClick={() => onPrintLetter()}
              fullWidth
              type="button"
            >
              <Box
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                Stampa lettera
              </Box>
            </PmiButton>
          </div>
        ) : undefined}
        {(typeRegister === TypeRegister.Emigration &&
          typePractice === TypePractice.EmigrationN010 &&
          formContext.values.practice?.codiceStatoPratica === ID_STATES_PRACTICE.POSITIVO &&
          ((formContext.values.practice?.tipoOperazione === '80' &&
            !formContext.values.practice?.notaOperazioneEmigrazioneN010) ||
            !formContext.values.practice?.operations?.find((el) => el.tipoOperazione === '31') ||
            (formContext.values.interestedPeople?.body?.some(
              (el) => el.personItem?.posizionePersona !== 'R'
            ) &&
              formContext.values.practice?.tipoOperazione === '31'))) ||
        (formContext.values.practice?.codiceStatoPratica === ID_STATES_PRACTICE.NEGATIVO &&
          formContext.values.practice?.tipoOperazione === '90' &&
          typeRegister === TypeRegister.Unavailable) ? (
          <div className="pmi-pb-2">
            <PmiButton
              variant="outlined"
              color="cdmBrandThird"
              onClick={() => setOpenN010Modal(true)}
              fullWidth
              type="button"
            >
              <Box
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                Nota di ripristino
              </Box>
            </PmiButton>
            <ModalN010
              open={openN010Modal}
              isIrreper={typeRegister === TypeRegister.Unavailable}
              onClose={() => setOpenN010Modal(false)}
              onSave={() => {
                //Chiamata a servizio e poi chiusura modale
                setOpenN010Modal(false);
              }}
            />
          </div>
        ) : undefined}
        <div className="pmi-pt-3">
          <PmiButton variant="outlined" color="cdmBrand" fullWidth onClick={handleExit}>
            Esci
          </PmiButton>
        </div>
      </div>
    </StickyDiv>
  );
};

ButtonsComponent.displayName = 'Reprint CID Page - Buttons';

const Buttons = memo(ButtonsComponent);

export default Buttons;
